import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Container,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import backArrow from "../../assets/icons/backArrow.svg";
import {
  CustomButton,
  CustomInput,
  CustomTable,
} from "../../global/components";
import CustomLoader from "../../global/components/CustomLoader/CustomLoader";
import CustomSelect from "../../global/components/CustomSelect/CustomSelect";
import strings from "../../global/constants/StringConstants";
import {
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import history from "../../utils/history";
import { theme } from "../../utils/styles";
import promoCodeStyles from "./PromoCode.styles";
import { generatePromo, validatePromoCode } from "./PromoCodeService";

const PromoCode = () => {
  const classes = promoCodeStyles;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accessType, setAccessType] = useState<string>("book-2");
  const [promo, setPromo] = useState<string>("");
  const [promoMessage, setPromoMessage] = useState<string>("");
  const [number, setNumber] = useState<number>(5);
  const [promoList, setPromoList] = useState<any[]>([]);

  const accessTypes = [
    { value: "book-2", content: "Book-2" },
    { value: "book-1", content: "Book-1" },
  ];

  const tableHeader = [
    {
      name: `${t("promoCode")}`,
      field: "promo",
    },
    {
      name: "",
      field: "actions",
      align: "center",
    },
  ];

  const validatePromo = async () => {
    if (!promo) {
      openErrorNotification("Please enter promo code.");
      return;
    }
    try {
      setIsLoading(true);
      const response = await validatePromoCode(promo);
      setPromoMessage(response.message);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (code: string) => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        openSuccessNotification(t("codeCopied"));
      })
      .catch(() => {
        openErrorNotification(t("copyFailed"));
      });
  };

  const generatePromoList = async () => {
    try {
      setIsLoading(true);
      const response = await generatePromo(accessType, number);
      const transformedData = response.codes.map((p: string) => {
        return {
          promo: p,
          actions: (
            <IconButton
              onClick={() => copyToClipboard(p)}
              sx={{
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          ),
        };
      });
      setPromoList(transformedData);
      openSuccessNotification(response.message);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getHeader = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { sm: "center", xs: "baseline" },
          width: "100%",
        }}
        pb={3}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ cursor: "pointer", width: "fit-content" }}
          onClick={() => history.goBack()}
        >
          {isMobile ? (
            <img src={backArrow} height={"20px"} alt="Module not found..." />
          ) : (
            <img src={backArrow} height={"25px"} alt="Module not found..." />
          )}
        </Box>
        <Typography sx={classes.italicTypo}>{t("PromoCode")}</Typography>
      </Box>
    );
  };

  const getValidatePromo = () => {
    return (
      <Stack direction={"row"} spacing={2} alignItems={"end"}>
        <Box sx={{ width: "500px" }}>
          <CustomInput
            label={t("validate")}
            value={promo}
            placeHolder="Enter promo code"
            onChange={(e: any) => {
              setPromo(e.target.value);
              if (!e.target.value) {
                setPromoMessage("");
              }
            }}
          />
        </Box>
        <CustomButton
          label={t("validate")}
          type={strings.PRIMARY}
          onClick={validatePromo}
          customClasses={{ maxWidth: "300px", minHight: "46px" }}
        />
      </Stack>
    );
  };

  const getGeneratePromo = () => {
    return (
      <Stack direction={"row"} spacing={2} alignItems={"end"} my={4}>
        <Box sx={{ width: "500px" }}>
          <CustomSelect
            label={t("generate")}
            value={accessType}
            menuItems={accessTypes}
            onChange={(e: any) => setAccessType(e.target.value)}
            id={"accessType"}
            name={"accessType"}
          />
        </Box>
        <CustomButton
          label={t("generate")}
          type={strings.PRIMARY}
          onClick={generatePromoList}
          customClasses={{ maxWidth: "300px", minHight: "46px" }}
        />
      </Stack>
    );
  };

  const getPromoCode = () => {
    return (
      <>
        <Box sx={classes.mainContainer}>
          <Box sx={classes.mainBox}>
            <Box sx={classes.subBox}>
              <Box sx={classes.scrollStyle}>
                <Container maxWidth="xl" sx={{ px: 1 }}>
                  {getHeader()}
                  {getValidatePromo()}
                  {promoMessage && (
                    <Typography sx={classes.promoMessage}>
                      {promoMessage}
                    </Typography>
                  )}
                  {getGeneratePromo()}
                  {promoList.length > 0 && (
                    <Box p={1}>
                      <CustomTable rows={promoList} headers={tableHeader} />
                    </Box>
                  )}
                </Container>
              </Box>
            </Box>
          </Box>
        </Box>
        <CustomLoader isLoading={isLoading} />
      </>
    );
  };
  return getPromoCode();
};

export default PromoCode;
